"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var extend_1 = tslib_1.__importDefault(require("extend"));
var Analytics_1 = tslib_1.__importDefault(require("../Analytics"));
var TrackerUtil_1 = tslib_1.__importDefault(require("./TrackerUtil"));
var BasicTrackerAbstractImpl_1 = tslib_1.__importDefault(require("./BasicTrackerAbstractImpl"));
var GTag = /** @class */ (function (_super) {
    tslib_1.__extends(GTag, _super);
    function GTag(options) {
        var _this = this;
        var _a;
        _this = _super.call(this, options) || this;
        _this.platformName = GTag.platformName;
        _this.isInterceptorSetup = false;
        _this.observers = new Set();
        _this.pageViewHasBeenReported = false; // Set to try after "page_view" but reset to false if popstate and pushstate
        // Setup interceptor - So we can notify observers.
        _this.setupInterceptor();
        // Log an error if "send_page_view" is not false when "config" is set.
        // Since it can be set through a few methods we use a "get" method of gtag().
        _this.addObserver({
            update: function (tracker, data) {
                _this.getLogger().debug("GTag Tracker Observed:", data);
                if (data.type === "set") {
                    if (typeof data.config === 'object' && _this.manualPageViewReporting === undefined && "send_page_view" in data.config) {
                        _this.manualPageViewReporting = !data.config["send_page_view"];
                    }
                }
                else if (data.type === "config") {
                    var gtag_1 = _this.getPlatformInstance();
                    if (!gtag_1) {
                        return;
                    }
                    gtag_1("get", data.targetId, "send_page_view", function (value) {
                        if (_this.manualPageViewReporting === undefined) {
                            _this.manualPageViewReporting = !value;
                        }
                    });
                }
                else if (data.type === "event") {
                    if (_this.manualPageViewReporting === undefined) {
                        console.error("Encountered a gtag() event call before the first configure call was sent");
                    }
                    if (data.eventName === "page_view") {
                        _this.pageViewHasBeenReported = true;
                    }
                    else if (!_this.pageViewHasBeenReported && _this.manualPageViewReporting) {
                        // Found first event that is not a "page_view"
                        // But we have not see a page_view reported yet. This is an issue!
                        throw new Error("An event other than \"page_view\" was reported to gtag prior to reporting a \"page_view\". You should NOT report an event until after \"page_view\".");
                    }
                }
            }
        });
        // On page ready report pageview (if not already reported)
        if (((_a = _this._options.trackPageviewTriggers) === null || _a === void 0 ? void 0 : _a.onPageLoaded) === true) {
            var onPageLoaded = function () {
                if (_this.manualPageViewReporting && !_this.doesDataLayerContainPageview()) {
                    _this.trackPageview();
                }
            };
            if (document.readyState === "interactive" || document.readyState === "complete") {
                onPageLoaded();
            }
            else {
                window.addEventListener("DOMContentLoaded", onPageLoaded);
            }
        }
        // Report pageview changes as result of popstate events
        // And keep track of if the page view has been reported or not yet.
        /*
        TODO: Need to override history.pushState() and history.replaceState() to intercept
            Kind of like: https://gist.github.com/rudiedirkx/fd568b08d7bffd6bd372
            But don't emit an event
            Then basically do this (which is the state as we do on "popstate" so
            it should maybe save code. We may need a setTimeout( ..., 0); too.
            () => {
                this.pageViewHasBeenReported = false;
                if (this._options.trackPageviewTriggers?.onHistoryStateChange === true) {
                    this.trackPageview();
                }
            }
        */
        window.addEventListener("popstate", function () {
            var _a;
            _this.pageViewHasBeenReported = false;
            if (_this.manualPageViewReporting && ((_a = _this._options.trackPageviewTriggers) === null || _a === void 0 ? void 0 : _a.onHistoryStateChange) === true) {
                // For why the timeout is needed see "setTimeout" on https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event
                // Basically the window.location is up to date already, but document.title may not be and that is reported inside: this.trackPageview();
                setTimeout(function () {
                    _this.trackPageview();
                }, 0);
            }
        });
        // Log information
        _this.getLogger().debug("".concat(_this.platformName, " instance initialized."), "Options: ", options);
        return _this;
    }
    GTag.getInstance = function (options) {
        if (!Analytics_1.default.isSetup()) {
            throw new Error("Analytics.setup() must be called once before you can get an instance of any tracker.");
        }
        var fullOptions = (0, extend_1.default)(true, {}, GTag.defaultOptions, options);
        var globalKeyForPlatform = fullOptions.globalKeyForPlatform;
        if (!globalKeyForPlatform) {
            throw new Error("Attempted to initialize GTag analytics the globalKeyForPlatform set to an empty value");
        }
        return TrackerUtil_1.default.getInstance(fullOptions, GTag.instances, function (options) { return new GTag(options); }, function () { return window[globalKeyForPlatform] !== undefined && window.dataLayer !== undefined; });
    };
    /**
     * @param pagePath - The path portion of a URL. Default: window.location.pathname
     * @param title - The title of the page (e.g. homepage). Default: document.title
     */
    GTag.prototype.trackPageview = function (pagePath, title) {
        if (pagePath === void 0) { pagePath = window.location.pathname; }
        if (title === void 0) { title = document.title; }
        if (!this.manualPageViewReporting) {
            console.error("Encountered a manual page_view tracking call on a site that has automatic page_view tracking enabled");
            return;
        }
        // TODO: This (pagePath = window.location.pathname) is not going to report hash.
        //  Should we consider reporting that too? Maybe, maybe not. Maybe we report as an event so we can tell when people click a link that scroll them down?
        pagePath = TrackerUtil_1.default.pagePathCleanup(pagePath);
        if (pagePath === this.lastReportedCurrentPageviewPath) {
            this.getLogger().debug("GTag.trackPageview: Not reporting to gtag this pageview because the same pagePath (\"".concat(pagePath, "\") was just reported."));
            return; // Do not report
        }
        var gtag = this.getPlatformInstance();
        if (!gtag) {
            return;
        }
        var params = {
            page_title: title,
            page_path: pagePath
        };
        gtag("event", "page_view", params);
        this.lastReportedCurrentPageviewPath = pagePath;
        // Log
        this.getLogger().debug("GTag.trackPageview:", params);
    };
    GTag.prototype.trackEvent = function (category, action, label, value, nonInteraction) {
        var eventAction = TrackerUtil_1.default.eventActionToString(action);
        var params = {
            event_category: category
        };
        if (label !== undefined && label !== null) {
            params.event_label = TrackerUtil_1.default.eventLabelToString(label);
        }
        if (value !== undefined && value !== null) {
            params.value = value;
        }
        if (nonInteraction !== undefined && nonInteraction !== null) {
            params.non_interaction = nonInteraction;
        }
        var gtag = this.getPlatformInstance();
        if (!gtag) {
            return;
        }
        gtag("event", eventAction, params);
        // Log
        this.getLogger().debug("GTag.trackEvent:", params);
    };
    GTag.prototype.getDataLayer = function () {
        return window.dataLayer; // TODO: This can be renamed: https://developers.google.com/tag-manager/devguide#renaming
    };
    GTag.prototype.setupInterceptor = function () {
        var _this = this;
        var dataLayer = this.getDataLayer();
        if (this.isInterceptorSetup) {
            return;
        }
        // Override the push() on dataLayer. This is what is called inside the gtag() method
        // Note: Overriding the push() is what Google deoes too.
        var previousPush = dataLayer.push;
        dataLayer.push = function () {
            var items = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                items[_i] = arguments[_i];
            }
            var result = previousPush.apply(dataLayer, items);
            items.forEach(function (dataLayerItem) {
                var data = GTag.dataLayerItemToObserverData(dataLayerItem);
                _this.notifyObservers(data);
            });
            return result;
        };
        this.isInterceptorSetup = true;
    };
    GTag.prototype.addObservers = function (observers, replayDataLayer) {
        var _this = this;
        if (replayDataLayer === void 0) { replayDataLayer = true; }
        observers.forEach(function (observer) { return _this.observers.add(observer); });
        if (replayDataLayer) {
            var dataLayer_1 = this.getDataLayer();
            dataLayer_1.forEach(function (dataLayerItem) {
                var data = GTag.dataLayerItemToObserverData(dataLayerItem);
                _this.notifyObservers(data, observers);
            });
        }
    };
    GTag.prototype.addObserver = function (observer, replayDataLayer) {
        if (replayDataLayer === void 0) { replayDataLayer = true; }
        this.addObservers([observer], replayDataLayer);
    };
    GTag.prototype.removeObserver = function (observer) {
        this.observers.delete(observer);
    };
    GTag.prototype.notifyObservers = function (data, observers) {
        var _this = this;
        if (observers === void 0) { observers = this.observers; }
        observers.forEach(function (observer) { return observer.update(_this, data); });
    };
    GTag.prototype.doesDataLayerContainPageview = function () {
        var dataLayer = this.getDataLayer();
        for (var i = 0; i < dataLayer.length; i++) {
            var dataLayerItem = dataLayer[i];
            var data = GTag.dataLayerItemToObserverData(dataLayerItem);
            // Since we require "send_page_view" to be set to false, the
            // only way pageviews can be reported is with events.
            if (data.type === "event" && data.eventName === "page_view") {
                return true;
            }
        }
        return false;
    };
    GTag.dataLayerItemToObserverData = function (dataLayerItem) {
        var data = null;
        // See documentation on dataLayer: https://developers.google.com/tag-manager/devguide
        if (dataLayerItem.length > 0 && dataLayerItem[0] !== undefined) { // Arguments are not an array so I can't finda better option then checking length to see if it is an "array" of arguments
            var type = dataLayerItem[0];
            // https://developers.google.com/gtagjs/reference/api
            if (type === "config") {
                //gtag('config', '<TARGET_ID>', {<additional_config_info>});
                data = {
                    type: type,
                    targetId: dataLayerItem[1],
                    config: dataLayerItem[2] // Could be undefined.
                };
            }
            else if (type === "get") {
                // gtag('get', '<target>', '<field_name>', callback)
                data = {
                    type: type,
                    targetId: dataLayerItem[1],
                    fieldName: dataLayerItem[2],
                    callback: dataLayerItem[3]
                };
            }
            else if (type === "set") {
                // gtag('set', '<target>', {<parameter-value-pair>, <parameter-value-pair>});
                // gtag('set', {<parameter-value-pair>, <parameter-value-pair>});
                if (typeof dataLayerItem[1] === "string") {
                    data = {
                        type: type,
                        targetId: dataLayerItem[1],
                        config: dataLayerItem[2]
                    };
                }
                else {
                    data = {
                        type: type,
                        config: dataLayerItem[1]
                    };
                }
            }
            else if (type === "js") {
                // gtag('js', new Date());
                data = {
                    type: type,
                    config: dataLayerItem[1]
                };
            }
            else if (type === "event") {
                // gtag('event', '<event_name>', {<event_params>});
                data = {
                    type: type,
                    eventName: dataLayerItem[1],
                    eventParams: dataLayerItem[2] // Could be undefined.
                };
            }
            else if (type === "consent") {
                // gtag('consent', {<consent_arg>}, {<consent_params>});
                data = {
                    type: type,
                    consentArg: dataLayerItem[1],
                    consentParams: dataLayerItem[2]
                };
            }
        }
        else {
            // Either an HTML event ({event: "event_name", ...}) or setting variable(s) ({"variable_name" : "variable_value", ...})
            // https://developers.google.com/tag-manager/devguide
            if (dataLayerItem.event !== undefined && typeof dataLayerItem.event === "string") {
                data = (0, extend_1.default)(true, { type: "htmlEvent" }, dataLayerItem);
            }
            else {
                data = (0, extend_1.default)(true, { type: "variables" }, dataLayerItem);
            }
        }
        return data;
    };
    GTag.platformName = "GTag";
    GTag.instances = new Map();
    GTag.defaultOptions = {
        globalKeyForPlatform: "gtag" // TODO: This could be renamed.
    };
    return GTag;
}(BasicTrackerAbstractImpl_1.default));
exports.default = GTag;
